import { Check, Info, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Compact pricing table for the public For Business / For Projects pages.
// rows: [{ band, price, note?, popular? }]
// features: [string | { label, info?, href?, hrefLabel? }], testidPrefix.
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const FeatureItem = ({ f, testidPrefix }) => {
  const item = typeof f === "string" ? { label: f } : f;
  const id = `${testidPrefix}-feature-${slug(item.label)}`;
  return (
    <li className="flex items-start gap-2.5 text-sm text-white/85" data-testid={id}>
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#7BD3CE]" />
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        {item.label}
        {item.info && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" aria-label={`What does "${item.label}" mean?`}
                data-testid={`${id}-info`}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/25 text-white/60 transition-colors hover:border-[#ECC764] hover:text-[#ECC764]">
                <Info className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" data-testid={`${id}-info-popover`}
              className="w-72 border-[#ECC764]/40 bg-[#04302E] p-4 text-xs leading-relaxed text-white/85 shadow-xl">
              {item.info}
            </PopoverContent>
          </Popover>
        )}
        {item.href && (
          <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={`${id}-link`}
            className="inline-flex items-center gap-1 text-xs font-bold text-[#ECC764] underline-offset-2 hover:underline">
            {item.hrefLabel || "View sample"} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </span>
    </li>
  );
};

export const PriceTable = ({ basisLabel, rows, features, testidPrefix }) => (
  <div className="mt-10 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`${testidPrefix}-table`}>
      <div className="grid grid-cols-[1fr_auto] items-center border-b border-white/10 bg-white/[0.04] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
        <span>{basisLabel}</span><span>One-time price</span>
      </div>
      {rows.map((r) => (
        <div key={r.band} data-testid={`${testidPrefix}-row-${slug(r.band)}`}
          className={"grid grid-cols-[1fr_auto] items-center gap-4 border-b border-white/5 px-5 py-3.5 last:border-b-0 " +
            (r.popular ? "bg-[#ECC764]/10" : "")}>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white">
              {r.band}
              {r.popular && (
                <span className="ml-2 rounded-full bg-[#ECC764] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-[#04302E]">Most common</span>
              )}
            </p>
            {r.note && <p className="text-xs text-white/50">{r.note}</p>}
          </div>
          <p className="text-lg font-extrabold tabular-nums text-white sm:text-xl">{r.price}</p>
        </div>
      ))}
    </div>
    <div className="rounded-2xl border border-[#ECC764]/40 bg-white/[0.06] p-7" data-testid={`${testidPrefix}-features`}>
      <h3 className="text-sm font-bold uppercase tracking-wider text-[#ECC764]">Every report includes</h3>
      <ul className="mt-5 space-y-3">
        {features.map((f) => (
          <FeatureItem key={typeof f === "string" ? f : f.label} f={f} testidPrefix={testidPrefix} />
        ))}
      </ul>
      <p className="mt-6 text-xs text-white/50">Your Get Started quiz score is free. Get your report once to start the 5-step journey.</p>
    </div>
  </div>
);
